import { QueryClient, QueryKey } from "@tanstack/react-query";
import {
  ChatDetailsView,
  WordView,
  OpenAiChatMessageView,
  PlatzWordTokenView,
  PlatzTokenView,
  PlatzBlobViewTokensItem,
  YouTubeTranscriptLineView,
  YoutubeTokenView,
  SourceLocation,
  YoutubeSourceLocation,
  TextSourceLocation,
  SourceLocationType,
  TextPageView,
  GoogledLyricsDetailsView,
  YouTubePageView,
} from "../api-generated";
import { SourceDetailsView } from "../shared";
import {
  isWordToken,
  isGoogledLyricsSource,
  isTextSource,
  isOpenAiSource,
  isYoutubeSource,
} from "../utils";

export const INTERESTING_THINGS_QUERY_KEY: QueryKey = [
  "HMM_INTERESTING_THINGS_DURP",
];

export const TAGGED_INSTANCES_QUERY_KEY: QueryKey = ["HMM_TAGGED_INSTANCEZZZ"];

export const getSourceQueryKeyPrefix = (sourceId: number): string =>
  `source-${sourceId}`;

export const getSourceQueryKey = (
  id: number,
  pageNumber?: number
): QueryKey => [getSourceQueryKeyPrefix(id), pageNumber];

export const updateChatCacheWithMessages = (
  sourceId: number,
  newMessages: OpenAiChatMessageView[],
  qc: QueryClient
): void => {
  const qk = getSourceQueryKey(sourceId);
  qc.setQueryData(qk, (old?: ChatDetailsView) => {
    if (old === undefined) {
      return undefined;
    }
    const newSource: ChatDetailsView = getNewChatSourceWithMessages(
      old,
      newMessages
    );
    return newSource;
  });
};

function getNewSourceCacheWithWords<T extends SourceDetailsView>(
  old: T,
  words: WordView[]
): SourceDetailsView {
  if (isGoogledLyricsSource(old)) {
    const rv: GoogledLyricsDetailsView = {
      ...old,
      lines: old.lines.map((l) => ({
        ...l,
        tokens: updateTokensWithWords(l.tokens, words),
      })),
    };
    return rv;
  }

  if (isYoutubeSource(old)) {
    const rv: YouTubePageView = {
      ...old,
      formattedSentences: old.formattedSentences?.map((s) => {
        return { ...s, tokens: updateYoutubeTokensWithWord(s.tokens, words) };
      }),
    };
    return rv;
  }

  if (isTextSource(old)) {
    const rv: TextPageView = {
      ...old,
      blobs: old.blobs!.map((b) => ({
        ...b,
        tokens: updateTokensWithWords(b.tokens, words),
      })),
    };
    return rv;
  }

  if (isOpenAiSource(old)) {
    const rv: ChatDetailsView = {
      ...old,
      messageViewList: old.messageViewList.map((message) => ({
        ...message,
        blobs: message.blobs.map((blob) => ({
          ...blob,
          tokens: updateTokensWithWords(blob.tokens, words),
        })),
      })),
    };
    return rv;
  }

  //TODO: why do I need this error?
  throw new Error("Can this even happen in typescript??");
}

export const updateSourceQueryCacheWithWords = (
  words: WordView[],
  qc: QueryClient,
  location: SourceLocation
) => {
  //TODO: this can be more abstract...
  let pageNumber = undefined;
  if (location.type === SourceLocationType.youtube) {
    pageNumber = (location as YoutubeSourceLocation).pageNumber;
  } else if (location.type === SourceLocationType.text_page) {
    pageNumber = (location as TextSourceLocation).pageNumber;
  }

  const qk = getSourceQueryKey(location.sourceId, pageNumber);

  qc.setQueryData(qk, (old?: SourceDetailsView) => {
    if (old === undefined) {
      return undefined;
    }
    const newSource: SourceDetailsView = getNewSourceCacheWithWords(old, words);
    return newSource;
  });
};

function updateTokensWithWords(
  tokens: PlatzBlobViewTokensItem[],
  words: WordView[]
): PlatzTokenView[] {
  return tokens.map((token: PlatzBlobViewTokensItem) => {
    if (!isWordToken(token)) {
      return token;
    }
    const wt = token as PlatzWordTokenView;
    const matchingWord = words.find((w) => w.id === wt.word!.id);
    if (matchingWord) {
      return { ...wt, word: matchingWord };
    }
    return wt;
  });
}

function updateYoutubeTokensWithWord(
  tokens: YoutubeTokenView[],
  words: WordView[]
): YoutubeTokenView[] {
  return tokens.map((ytToken: YoutubeTokenView) => {
    if (!isWordToken(ytToken.token)) {
      return ytToken;
    }
    const wt = ytToken.token as PlatzWordTokenView;
    const matchingWord = words.find((w) => w.id === wt.word!.id);
    if (matchingWord) {
      return { ...ytToken, token: { ...ytToken.token, word: matchingWord } };
    }
    return { ...ytToken, token: wt };
  });
}

const getNewChatSourceWithMessages = (
  old: ChatDetailsView,
  newMessages: OpenAiChatMessageView[]
): ChatDetailsView => ({
  ...old,
  messageViewList: [...old.messageViewList, ...newMessages],
});

export function updateYoutubeCacheWithTranslations(
  sourceId: number,
  pageNumber: number,
  translations: string[],
  qc: QueryClient
): void {
  const queryKey = getSourceQueryKey(sourceId, pageNumber);

  qc.setQueryData(
    queryKey,
    (old?: YouTubePageView): YouTubePageView | undefined => {
      if (old === undefined) {
        return undefined;
      }
      return {
        ...old,
        formattedSentences: updateLinesWithTranslations(
          old.formattedSentences!,
          translations
        ),
      };
    }
  );
}

const updateLinesWithTranslations = (
  lines: YouTubeTranscriptLineView[],
  translations: string[]
): YouTubeTranscriptLineView[] => {
  return lines.map((l, i) => ({
    ...l,
    englishTranslation: translations[i],
  }));
};

export function updateYoutubeCacheWithPage(
  sourceId: number,
  pageNumber: number,
  newPage: YouTubePageView,
  qc: QueryClient
): void {
  const queryKey = getSourceQueryKey(sourceId, pageNumber);
  qc.setQueryData(queryKey, newPage);
}
